'use client';

import './styles.css';

import clsx from 'clsx';
import { useState } from 'react';

import { ChevronBottomIcon } from '@/shared';

import type { ITrainingProgramm, ITrainingProgrammItem } from '../../types';

const TrainingProgrammV2Item = ({ item }: { item: ITrainingProgrammItem }) => {
	const [open, setOpen] = useState(false);

	const wrapClassName = clsx('tranining-programm-v2 group', {
		'hover:shadow-topic': !open,
		'shadow-topic': open,
	});

	const bodyClassName = clsx(
		'max-h-4 overflow-hidden transition-all duration-500 ease-in-out',
		{
			'group-hover:max-h-20 group-hover:shadow-[inset_0_2px_0_0_#DBDFE9]':
				!open,
			'max-h-[1000px] shadow-[inset_0_2px_0_0_#DBDFE9]': open,
		},
	);

	const buttonClassName = clsx('tranining-programm-v2-button', {
		'group-hover:pt-8': !open,
		'open rotate-180': open,
	});

	return (
		<div className={wrapClassName} onClick={() => setOpen(!open)}>
			<div className="flex w-full flex-col items-start p-4 pb-8 xl:p-6 2xl:px-12 2xl:py-8">
				<ul className="flex flex-row flex-wrap gap-2 text-base/[1.4] text-[#99A1B7] max-xl:hidden xl:pr-[140px]">
					{item.labels
						.reduce<string[]>(
							(acc, val) => (acc.length ? [...acc, '/', val] : [val]),
							[],
						)
						.map((label, k) => (
							<li key={k}>{label}</li>
						))}
				</ul>
				<div className="mt-1 text-left text-base/[1.2] font-semibold xl:text-2xl 2xl:text-[32px]/[1.3]">
					{item.title}
				</div>
				{item.tags?.length && (
					<ul className="mt-4 flex flex-row flex-wrap gap-2 text-sm/[1.6]">
						{item.tags.map((t, k) => {
							const cl = clsx('rounded px-1.5', {
								'bg-[#41B522] text-white': k === 0,
								'bg-[#000000]/5 text-gray-3': k !== 0,
							});
							return (
								<li key={k} className={cl}>
									{t}
								</li>
							);
						})}
					</ul>
				)}
				<div className={buttonClassName}>
					<ChevronBottomIcon className="relative z-10 mx-auto size-6 text-gray-2" />
				</div>
			</div>
			<div className={bodyClassName}>
				<div className="px-4 pb-10 pt-4 xl:px-6 xl:pb-10 xl:pt-6 2xl:px-12">
					<ul className="flex flex-col gap-2 text-sm/[1.6] text-[#99A1B7] xl:hidden">
						{item.labels.map((label, k) => (
							<li key={k}>{label}</li>
						))}
					</ul>
					<div className="flex flex-col gap-6 xl:flex-row xl:justify-between xl:gap-6">
						{item.items.map(({ title, content }, k) => (
							<div
								key={k}
								className="text-xs/[1.6] text-[#4B5675] xl:flex-1 xl:text-sm/[1.6]"
							>
								<h3 className="text-sm/[1.6] font-semibold text-[#252F4A] xl:text-base/[1.6]">
									{title}
								</h3>
								{Array.isArray(content) ? (
									<ul className="ml-4 mt-4 list-disc space-y-2">
										{content.map((c, k) => (
											<li key={k}>{c}</li>
										))}
									</ul>
								) : (
									<p className="mt-4">{content}</p>
								)}
							</div>
						))}
					</div>
					<p className="mt-4 text-sm/[1.6] font-semibold text-[#4B5675] xl:mt-10 xl:text-base/[1.6]">
						{item.text}
					</p>
				</div>
			</div>
		</div>
	);
};

export const TrainingProgrammV2 = ({
	programs,
}: {
	programs: ITrainingProgramm['programs'];
}) => {
	return (
		<div className="flex flex-col gap-2 md:grid-cols-3">
			{programs.map((item, key) => (
				<TrainingProgrammV2Item key={key} item={item} />
			))}
		</div>
	);
};
